/**
 * ZIP export of the workbook.
 *
 * Every sheet is exported as a separate `<sheetName>.csv` file inside the
 * archive. Sheets whose column signature matches are merged into a single
 * CSV in the archive so the round-trip — ZIP-import → ZIP-export — preserves
 * the user's "same format means same file" mental model.
 *
 * Built on `fflate`'s synchronous `zip` (it runs in a Web Worker under the
 * hood, off the main thread, with a 50 ms one-time overhead).
 */

import { zipSync, strToU8 } from 'fflate';
import type { SheetId } from '../core/types.js';
import type { KernelClient } from '../kernel/client.js';
import { exportSheet, type ExportFormat } from './exportService.js';
import { sheetColumnSignature } from './zipStreamImport.js';

export interface ZipExportOptions {
  /** Workbook sheets to export. If omitted, all sheets in the workbook. */
  sheets?: readonly SheetId[] | undefined;
  /** Per-sheet row limit; defaults to 5,000,000. */
  rowLimit?: number | undefined;
  onProgress?: ((info: { sheet: string; rows: number }) => void) | undefined;
}

export interface ZipExportResult {
  blob: Blob;
  files: Array<{ name: string; rows: number; cells: number }>;
  /** Sheets that shared a column signature and were merged into one CSV. */
  mergedSheets: Array<{ signature: string; fileName: string; sheets: string[] }>;
}

/** Exports a workbook to a single ZIP of CSV files, merging same-format sheets. */
export async function exportWorkbookZip(
  client: KernelClient,
  opts: ZipExportOptions,
): Promise<ZipExportResult> {
  // Discover the workbook's sheets if the caller did not pin a subset.
  let sheets = opts.sheets;
  if (sheets === undefined || sheets.length === 0) {
    let view;
    try {
      view = await client.sheetView('sheet-1');
    } catch {
      view = await client.sheetView('');
    }
    const known = (view.sheets ?? []).map((s) => s.id);
    sheets = known.length > 0 ? known : [view.sheet || 'sheet-1'];
  }

  // First pass: export every sheet to CSV in memory, record each sheet's
  // column signature so we can group identical ones.
  type SheetExport = {
    sheetId: SheetId;
    sheetName: string;
    format: ExportFormat;
    text: string;
    rows: number;
    cells: number;
    signature: string;
  };
  const exports: SheetExport[] = [];

  for (const sheetId of sheets) {
    const view = await client.sheetView(sheetId);
    // Use the formatted values as the source for the column signature so
    // "format same" really means "displayed columns line up".
    const headerCols = view.usedRange
      ? await headerColumnsOf(client, sheetId, view.usedRange)
      : [];
    const result = await exportSheet(client, {
      format: 'csv',
      sheet: sheetId,
      rowLimit: opts.rowLimit,
      onProgress: ({ rows }) => opts.onProgress?.({ sheet: view.name, rows }),
    });
    exports.push({
      sheetId,
      sheetName: view.name,
      format: 'csv',
      text: result.text,
      rows: result.rows,
      cells: result.cells,
      signature: sheetColumnSignature(headerCols),
    });
  }

  // Group by signature so identical layouts merge into one archive entry.
  const groups = new Map<string, SheetExport[]>();
  for (const exp of exports) {
    const arr = groups.get(exp.signature);
    if (arr !== undefined) {
      arr.push(exp);
    } else {
      groups.set(exp.signature, [exp]);
    }
  }

  // Build the ZIP payload. Group-merged entries concatenate bodies (skipping
  // the trailing header line of every body after the first so the merged CSV
  // has exactly one header row at the top).
  const files: ZipExportResult['files'] = [];
  const merged: ZipExportResult['mergedSheets'] = [];
  const zipInput: Record<string, Uint8Array> = {};

  for (const [signature, group] of groups) {
    if (group.length === 1) {
      const exp = group[0] as SheetExport;
      const fileName = `${sanitize(exp.sheetName)}.csv`;
      zipInput[fileName] = strToU8(exp.text);
      files.push({ name: fileName, rows: exp.rows, cells: exp.cells });
      continue;
    }
    // Multi-sheet group: merge.
    const first = group[0] as SheetExport;
    const fileName = `${sanitize(first.sheetName)}_merged.csv`;
    const mergedText = mergeCsvBodies(group);
    zipInput[fileName] = strToU8(mergedText);
    const totalRows = group.reduce((s, g) => s + g.rows, 0);
    const totalCells = group.reduce((s, g) => s + g.cells, 0);
    files.push({ name: fileName, rows: totalRows, cells: totalCells });
    merged.push({
      signature,
      fileName,
      sheets: group.map((g) => g.sheetName),
    });
  }

  const zipBytes = zipSync(zipInput, { level: 6 });
  const blob = new Blob([zipBytes], { type: 'application/zip' });

  return { blob, files, mergedSheets: merged };
}

/** Triggers a browser download of a workbook ZIP. */
export function downloadWorkbookZip(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Sanitizes a sheet name into a safe CSV filename. */
function sanitize(name: string): string {
  return name.replace(/[\\/?*[\]:]/g, '_').slice(0, 100) || 'sheet';
}

/**
 * Reads the column header row of the used range so the ZIP exporter can
 * decide whether two sheets share a layout. Only the first row is sampled,
 * which matches the import side's heuristic.
 */
async function headerColumnsOf(
  client: KernelClient,
  sheet: SheetId,
  usedRange: { r0: number; c0: number; c1: number },
): Promise<string[]> {
  // No usedRange → no columns.
  if (usedRange.c1 < usedRange.c0) return [];
  const range = { sheet, r0: usedRange.r0, c0: usedRange.c0, r1: usedRange.r0, c1: usedRange.c1 };
  const tile = await client.tile(range);
  const width = range.c1 - range.c0 + 1;
  const cols: string[] = [];
  for (let i = 0; i < width; i++) {
    const v = (tile as { values?: readonly { k: string; v?: unknown }[] }).values?.[i];
    const text = v !== undefined && v.k === 'text' ? String(v.v ?? '') : '';
    cols.push(text);
  }
  return cols;
}

/**
 * Concatenates multiple CSV bodies into one. RFC-4180 quoting means a
 * simple string concat works when all bodies share a delimiter, but we
 * strip the trailing header from every body after the first to leave a
 * single header row at the top.
 */
function mergeCsvBodies(groups: readonly { text: string }[]): string {
  if (groups.length === 0) return '';
  const first = groups[0];
  if (first === undefined) return '';
  const parts: string[] = [first.text];
  for (let i = 1; i < groups.length; i++) {
    const body = groups[i]?.text ?? '';
    if (body.length === 0) continue;
    // Drop the first line (header) of every subsequent body.
    const nl = body.indexOf('\n');
    const tail = nl === -1 ? '' : body.slice(nl + 1);
    parts.push(tail);
  }
  return parts.join('\n').replace(/\n+$/, '\n');
}

// Re-export for symmetry with the import side.
export { sheetColumnSignature };
